import { goto } from '$app/navigation'
import type { ModalSettings, ModalStore, ToastStore } from '@skeletonlabs/skeleton'
import type { QueryFunction } from '@tanstack/svelte-query'
import camelcaseKeys from 'camelcase-keys'
import { get } from 'svelte/store'

import {
  type IRawUnlearningRun,
  type IUnlearningRun,
  type RunInfo,
  serializeRunInfoForApi,
} from './llm-run.svelte'
import type { ICreateLlm } from '../llms/llms-store.svelte'
import { llmsStore } from '../llms/llms-store.svelte'
import { getModelSourceName } from '../llms/restricted-models'
import type { Config } from '@utils/config.svelte'
import { getConfig } from '@utils/config.svelte'
import { encodeHTMLEntities } from '@utils/encode'
import { FetchError, handleFetch } from '@utils/fetch'
import { modalRegistry } from '@utils/modal-registry.svelte'
import { prepareRestorableModal } from '@utils/modal-restore'
import { queryClient } from '@utils/svelte-query-client'

const parseUnlearningRun = (unlearningRun: IRawUnlearningRun): IUnlearningRun => ({
  ...unlearningRun,
  createdAt: new Date(unlearningRun.createdAt),
  completedAt: unlearningRun.completedAt ? new Date(unlearningRun.completedAt) : null,
  deletedAt: unlearningRun.deletedAt ? new Date(unlearningRun.deletedAt) : null,
})

const llmUnlearningRunsQuery: ({
  archived,
}: {
  archived?: boolean
}) => QueryFunction<Map<number, IUnlearningRun>> =
  ({ archived = false }: { archived?: boolean }) =>
  async ({ signal }) => {
    try {
      const unlearningRuns: IRawUnlearningRun[] = camelcaseKeys(
        await (
          await handleFetch(`/unlearning-llm/run/list?archived=${archived}`, {
            signal,
          })
        ).json(),
        { deep: true },
      )
      const unlearningRunMap = unlearningRuns.reduce((acc, unlearningRun) => {
        acc.set(unlearningRun.id, parseUnlearningRun(unlearningRun))
        return acc
      }, new Map<number, IUnlearningRun>())
      return unlearningRunMap
    } catch (err) {
      console.error('Failed to load LLM unlearning runs: ', err)
      throw err
    }
  }

const llmUnlearningRunsQueryKey = (archived?: boolean) => ['llm-unlearning-runs', archived]

type RunOptionsMeta = {
  llmId: number
  llmName: string
  modelSource?: unknown
  config: Config | null
}

type OpenRunOptionsModalParams = {
  modalStore: ModalStore
  toastStore: ToastStore
  runOptions: RunOptionsMeta
  model?: $state.Snapshot<ICreateLlm>
  fallbackConfig?: Config | null
}

/**
 * Loads the LLM unlearning runs from the server.
 * Invalidates the query cache to ensure the data is fresh.
 */
const loadUnlearningRuns = async ({ archived = false }: { archived?: boolean } = {}) => {
  await queryClient.invalidateQueries({ queryKey: llmUnlearningRunsQueryKey(archived) })
  await queryClient.fetchQuery({
    queryKey: llmUnlearningRunsQueryKey(archived),
    queryFn: llmUnlearningRunsQuery({ archived }),
  })
}

const withEffectiveConfig = async (
  runOptions: RunOptionsMeta,
  fallbackConfig?: Config | null,
): Promise<RunOptionsMeta> => ({
  ...runOptions,
  config: runOptions.config ?? fallbackConfig ?? (await getConfig().catch(() => null)),
})

const getRunOptionsDisplayName = (
  runOptions: RunOptionsMeta,
  model?: $state.Snapshot<ICreateLlm>,
) => {
  const storedLlm = get(llmsStore).get(runOptions.llmId)
  return (
    runOptions.llmName ||
    storedLlm?.modelName ||
    model?.modelName ||
    getModelSourceName(runOptions.modelSource ?? storedLlm?.modelSource ?? model?.modelSource) ||
    `Model #${runOptions.llmId}`
  )
}

const openRunOptionsModal = async ({
  modalStore,
  toastStore,
  runOptions,
  model,
  fallbackConfig,
}: OpenRunOptionsModalParams) => {
  const effectiveRunOptions = {
    ...(await withEffectiveConfig(runOptions, fallbackConfig)),
    llmName: getRunOptionsDisplayName(runOptions, model),
  }

  modalStore.trigger({
    type: 'component',
    component: modalRegistry.llmRunOptionsModal,
    title: 'Run options',
    body: '',
    value: effectiveRunOptions,
    response: async (action: 'unlearn' | 'eval' | false) => {
      if (action === 'unlearn') {
        const llm = model ?? get(llmsStore).get(effectiveRunOptions.llmId)
        if (!llm) {
          console.error('Failed to find LLM when reopening unlearning modal', runOptions.llmId)
          return
        }
        await runUnlearning(
          llm,
          effectiveRunOptions.llmId,
          toastStore,
          modalStore,
          effectiveRunOptions.llmName,
          effectiveRunOptions.config,
          { runOptions: effectiveRunOptions },
        )
      } else if (action === 'eval') {
        modalStore.trigger({
          type: 'component',
          component: modalRegistry.launchEvalModal,
          value: {
            modelId: effectiveRunOptions.llmId,
            title: `Evaluation ${effectiveRunOptions.llmName}`,
          },
          meta: {
            runOptions: effectiveRunOptions,
          },
        })
      }
    },
  })
}

const runUnlearning = async (
  model: $state.Snapshot<ICreateLlm>,
  modelId: number,
  toastStore: ToastStore,
  modalStore: ModalStore,
  modelName: string | undefined,
  config: Config | null,
  options?: { runOptions?: RunOptionsMeta },
) => {
  const modal: ModalSettings = {
    type: 'component',
    component: modalRegistry.runLLMUnlearningModal,
    // Data
    title: 'Run model unlearning',
    body: 'Please choose the type of unlearning to run',
    buttonTextConfirm: 'Run now',
    value: model,
    meta: options?.runOptions
      ? {
          runOptions: options.runOptions,
        }
      : undefined,

    response: async (runInfo: RunInfo | 'back-to-run-options') => {
      if (runInfo === 'back-to-run-options') {
        const fallbackName = modelName ?? get(llmsStore).get(modelId)?.modelName ?? ''
        const runOptions =
          options?.runOptions ??
          ({
            llmId: modelId,
            llmName: fallbackName,
            modelSource: get(llmsStore).get(modelId)?.modelSource,
            config,
          } satisfies RunOptionsMeta)
        await openRunOptionsModal({
          modalStore,
          toastStore,
          runOptions,
          model,
          fallbackConfig: config,
        })
        return
      }

      if (!runInfo) {
        return
      }
      try {
        await Promise.all([
          handleFetch(`/unlearning-llm/run/${modelId}`, {
            method: 'POST',
            body: serializeRunInfoForApi(runInfo),
          }),
          new Promise((resolve) => setTimeout(resolve, 1_000)),
        ])
        toastStore.trigger({
          message: `Started model unlearning run for <strong>${encodeHTMLEntities(modelName ?? '')}</strong>`,
          background: 'variant-filled-success',
        })
        // Invalidate the query to refetch fresh data
        queryClient.invalidateQueries({ queryKey: llmUnlearningRunsQueryKey(false) })
        await goto(`/llm/runs`)
      } catch (err) {
        console.error(err)
        if (err instanceof FetchError && err.message) {
          toastStore.trigger({
            message: encodeHTMLEntities(
              `Failed to launch model unlearning run with error: ${err.message}`,
            ),
            background: 'variant-filled-error',
            autohide: false,
          })
          throw err
        }
        toastStore.trigger({
          message: 'Failed to launch model behavior unlearning run',
          background: 'variant-filled-error',
        })
        throw err
      }
    },
  }
  modalStore.trigger(
    prepareRestorableModal(modalStore, toastStore, modal, {
      toastMessage: 'Run model unlearning closed. Reopen to restore your changes?',
    }),
  )
}

const archiveRun = async (runId: string) => {
  await (
    await handleFetch(`/unlearning-llm/run/archive/${runId}`, {
      method: 'PATCH',
    })
  ).json()
}

const restoreRun = async (runId: string) => {
  await (
    await handleFetch(`/unlearning-llm/run/restore/${runId}`, {
      method: 'PATCH',
    })
  ).json()
}

export {
  archiveRun,
  llmUnlearningRunsQuery,
  llmUnlearningRunsQueryKey,
  loadUnlearningRuns,
  openRunOptionsModal,
  restoreRun,
  runUnlearning,
}
export type { RunOptionsMeta }
